import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import { Edit as EditIcon, Delete as DeleteIcon, Plus as PlusIcon, Laptop, CheckSquare, Search, Users as UsersLogoIcon } from 'lucide-react';
import SmartWorkingModal from '../components/SmartWorkingModal';
import AddIcon from '@mui/icons-material/Add';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import BlockIcon from '@mui/icons-material/Block';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import BusinessIcon from '@mui/icons-material/Business';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
}));

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const validationSchema = Yup.object({
  name: Yup.string().required('Il nome è obbligatorio'),
  email: Yup.string().email('Email non valida').required('L\'email è obbligatoria'),
  password: Yup.string().min(6, 'La password deve avere almeno 6 caratteri'),
});

export default function Users() {
  const [users, setUsers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [smartWorkingModalOpen, setSmartWorkingModalOpen] = useState(false);
  const [selectedUserForSmartWorking, setSelectedUserForSmartWorking] = useState(null);
  
  // Bulk Actions State
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkIpModalOpen, setBulkIpModalOpen] = useState(false);
  const [bulkIpAddress, setBulkIpAddress] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [bulkSuccess, setBulkSuccess] = useState('');
  const [bulkDeptModalOpen, setBulkDeptModalOpen] = useState(false);
  const [bulkDeptId, setBulkDeptId] = useState('');
  const [bulkBlockModalOpen, setBulkBlockModalOpen] = useState(false);
  const [bulkBlockAction, setBulkBlockAction] = useState('block'); // 'block' or 'unblock'
  const [bulkBlockDate, setBulkBlockDate] = useState('');
  const [bulkSmartWorkingModalOpen, setBulkSmartWorkingModalOpen] = useState(false);
  const [bulkSmartWorkingAction, setBulkSmartWorkingAction] = useState('enable'); // 'enable' or 'disable'

  const [filterRole, setFilterRole] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterOffice, setFilterOffice] = useState('');
  const [filterBlocked, setFilterBlocked] = useState('');
  const [filterSmartWorking, setFilterSmartWorking] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users.map((n) => n.id);
      setSelectedUsers(newSelecteds);
      return;
    }
    setSelectedUsers([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedUsers.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUsers, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelected = newSelected.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1),
      );
    }

    setSelectedUsers(newSelected);
  };

  const isSelected = (id) => selectedUsers.indexOf(id) !== -1;

  const handleBulkIpSubmit = async () => {
    if (!bulkIpAddress) return;
    
    // Simple IP validation
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(bulkIpAddress)) {
        setBulkError('Indirizzo IP non valido');
        return;
    }

    try {
      await api.post('/users/bulk-smart-working-ip', {
        userIds: selectedUsers,
        ip: bulkIpAddress
      });
      setBulkSuccess(`IP aggiunto con successo a ${selectedUsers.length} utenti`);
      setBulkIpModalOpen(false);
      setBulkIpAddress('');
      setSelectedUsers([]);
      // Optionally refresh data if needed, but not strictly required for list view
    } catch (err) {
      setBulkError('Errore durante l\'aggiornamento: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSelectAllFilteredUsers = async () => {
    try {
      const params = {
        page: 1,
        limit: totalItems || 10000,
        search,
        sort: orderBy,
        order: order.toUpperCase(),
      };

      if (filterRole) {
        params.role_id = filterRole;
      }

      if (filterDepartment) {
        params.department_id = filterDepartment;
      }

      if (filterOffice) {
        params.office_id = filterOffice;
      }
      if (filterBlocked) {
        params.blocked = filterBlocked === 'blocked' ? 1 : filterBlocked === 'unblocked' ? 0 : '';
      }
      if (filterSmartWorking) {
        params.smart_working = filterSmartWorking === 'enabled' ? 1 : filterSmartWorking === 'disabled' ? 0 : '';
      }

      const res = await api.get('/users', { params });
      const all = (res.data.users || []).map((u) => u.id);
      setSelectedUsers(all);
      toast.success(`Selezionati ${all.length} utenti (in base ai filtri correnti)`);
    } catch (error) {
      console.error('Errore nel caricamento di tutti gli utenti per la selezione IP:', error);
      toast.error('Errore nel selezionare tutti gli utenti');
    }
  };

  const handleBulkDeptSubmit = async () => {
    if (!bulkDeptId) {
      toast.error('Seleziona un dipartimento');
      return;
    }
    try {
      let targetIds = selectedUsers;
      if (targetIds.length === 0) {
        const params = {
          page: 1,
          limit: totalItems || 10000,
          search,
          sort: orderBy,
          order: order.toUpperCase(),
        };
        if (filterRole) params.role_id = filterRole;
        if (filterDepartment) params.department_id = filterDepartment;
        if (filterOffice) params.office_id = filterOffice;
        if (filterBlocked) params.blocked = filterBlocked === 'blocked' ? 1 : filterBlocked === 'unblocked' ? 0 : '';
        if (filterSmartWorking) params.smart_working = filterSmartWorking === 'enabled' ? 1 : filterSmartWorking === 'disabled' ? 0 : '';
        const resAll = await api.get('/users', { params });
        targetIds = (resAll.data.users || []).map((u) => u.id);
      }

      const res = await api.post('/users/bulk-assign-department', {
        userIds: targetIds,
        department_id: bulkDeptId
      });
      toast.success(`Dipartimento assegnato a ${res.data.updatedCount} utenti`);
      setBulkDeptModalOpen(false);
      setBulkDeptId('');
      fetchUsers();
    } catch (error) {
      console.error('Errore assegnazione dipartimento:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'assegnazione del dipartimento');
    }
  };

  const handleBulkBlockSubmit = async () => {
    try {
      let targetIds = selectedUsers;
      if (targetIds.length === 0) {
        const params = {
          page: 1,
          limit: totalItems || 10000,
          search,
          sort: orderBy,
          order: order.toUpperCase(),
        };
        if (filterRole) params.role_id = filterRole;
        if (filterDepartment) params.department_id = filterDepartment;
        if (filterOffice) params.office_id = filterOffice;
        if (filterBlocked) params.blocked = filterBlocked === 'blocked' ? 1 : filterBlocked === 'unblocked' ? 0 : '';
        if (filterSmartWorking) params.smart_working = filterSmartWorking === 'enabled' ? 1 : filterSmartWorking === 'disabled' ? 0 : '';
        const resAll = await api.get('/users', { params });
        targetIds = (resAll.data.users || []).map((u) => u.id);
      }

      const payload = {
        userIds: targetIds,
        block_access: bulkBlockAction === 'block',
        block_date: bulkBlockAction === 'block' ? (bulkBlockDate || null) : null,
      };
      const res = await api.post('/users/bulk-block-account', payload);
      toast.success(`${bulkBlockAction === 'block' ? 'Blocco' : 'Sblocco'} applicato a ${res.data.updatedCount} utenti`);
      setBulkBlockModalOpen(false);
      setBulkBlockAction('block');
      setBulkBlockDate('');
      fetchUsers();
    } catch (error) {
      console.error('Errore blocco/sblocco account:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'operazione di blocco/sblocco');
    }
  };

  const handleBulkSmartWorkingSubmit = async () => {
    try {
      let targetIds = selectedUsers;
      if (targetIds.length === 0) {
        const params = {
          page: 1,
          limit: totalItems || 10000,
          search,
          sort: orderBy,
          order: order.toUpperCase(),
        };
        if (filterRole) params.role_id = filterRole;
        if (filterDepartment) params.department_id = filterDepartment;
        if (filterOffice) params.office_id = filterOffice;
        if (filterBlocked) params.blocked = filterBlocked === 'blocked' ? 1 : filterBlocked === 'unblocked' ? 0 : '';
        if (filterSmartWorking) params.smart_working = filterSmartWorking === 'enabled' ? 1 : filterSmartWorking === 'disabled' ? 0 : '';
        const resAll = await api.get('/users', { params });
        targetIds = (resAll.data.users || []).map((u) => u.id);
      }

      const enabled = bulkSmartWorkingAction === 'enable';
      const res = await api.post('/users/bulk-smart-working-toggle', {
        userIds: targetIds,
        enabled,
      });

      toast.success(`Smart Working ${enabled ? 'abilitato' : 'disabilitato'} per ${res.data.updatedCount} utenti`);
      setBulkSmartWorkingModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Errore abilitazione/disabilitazione smart working:', error);
      toast.error(error.response?.data?.message || 'Errore durante l\'operazione Smart Working');
    }
  };

  const handleSmartWorkingClick = (user) => {
    setSelectedUserForSmartWorking(user);
    setSmartWorkingModalOpen(true);
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get('/roles');
      setRoles(res.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/app-departments');
      setDepartments(res.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Errore nel caricamento dei dipartimenti');
    }
  };

  const fetchOffices = async () => {
    try {
      const res = await api.get('/app-offices');
      setOffices(res.data);
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchDepartments();
    fetchOffices();
  }, []);

  const fetchUsers = React.useCallback(async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        sort: orderBy,
        order: order.toUpperCase(),
      };

      if (filterRole) {
        params.role_id = filterRole;
      }

      if (filterDepartment) {
        params.department_id = filterDepartment;
      }

      if (filterOffice) {
        params.office_id = filterOffice;
      }
      if (filterBlocked) {
        params.blocked = filterBlocked === 'blocked' ? 1 : filterBlocked === 'unblocked' ? 0 : '';
      }
      if (filterSmartWorking) {
        params.smart_working = filterSmartWorking === 'enabled' ? 1 : filterSmartWorking === 'disabled' ? 0 : '';
      }

      const res = await api.get('/users', { params });
      setUsers(res.data.users);
      setTotalItems(res.data.totalItems);
    } catch (error) {
      console.error(error);
      toast.error('Errore nel caricamento degli utenti');
    }
  }, [page, rowsPerPage, search, orderBy, order, filterRole, filterDepartment, filterOffice, filterBlocked, filterSmartWorking]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      password: '',
      role_id: '',
      department_id: '',
      office_id: '',
      block_access: false,
      block_date: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        if (currentId) {
          // If password is empty in edit mode, don't send it
          const dataToSend = { ...values };
          if (!dataToSend.password) {
            delete dataToSend.password;
          }
          if (!dataToSend.block_access) {
            dataToSend.block_date = null;
          }
          await api.put(`/users/${currentId}`, dataToSend);
          toast.success('Utente aggiornato con successo');
        } else {
          if (!values.password) {
            toast.error('La password è obbligatoria per i nuovi utenti');
            return;
          }
          const payload = { ...values };
          if (!payload.block_access) {
            payload.block_date = null;
          }
          await api.post('/users', payload);
          toast.success('Utente creato con successo');
        }
        handleClose();
        fetchUsers();
      } catch (error) {
        console.error(error);
        toast.error(error.response?.data?.message || 'Errore nel salvataggio dell\'utente');
      }
    },
  });

  const handleOpen = (user = null) => {
    if (user) {
      setCurrentId(user.id);
      formik.setValues({
        name: user.name,
        email: user.email,
        password: '', // Always empty for security
        role_id: user.role_id || '',
        department_id: user.department_id || '',
        office_id: user.office_id || '',
        block_access: !!user.block_access,
        block_date: user.block_access ? (user.block_date ? user.block_date.replace(' ', 'T').slice(0, 16) : '') : '',
      });
    } else {
      setCurrentId(null);
      formik.resetForm();
      // Set default role if available (e.g., 'user')
      const defaultRole = roles.find(r => r.name === 'user');
      if (defaultRole) {
        formik.setFieldValue('role_id', defaultRole.id);
      }
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const availableOffices = React.useMemo(() => {
    const deptId = formik.values.department_id;
    if (!deptId) return [];
    const numericDeptId = parseInt(deptId, 10);
    if (Number.isNaN(numericDeptId)) return [];
    return offices.filter((office) => office.department_id === numericDeptId);
  }, [offices, formik.values.department_id]);

  useEffect(() => {
    if (!formik.values.office_id) return;
    const numericOfficeId = parseInt(formik.values.office_id, 10);
    const office = offices.find((o) => o.id === numericOfficeId);
    const deptId = formik.values.department_id ? parseInt(formik.values.department_id, 10) : null;
    if (office && deptId && office.department_id !== deptId) {
      formik.setFieldValue('office_id', '');
    }
  }, [formik.values.department_id, formik.values.office_id, offices]);

  const openDeleteDialog = (user) => {
    setDeleteTarget(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success('Utente eliminato con successo');
      closeDeleteDialog();
      fetchUsers();
    } catch (error) {
      console.error(error);
      toast.error('Errore nella cancellazione dell\'utente');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activeFiltersCount = [
    !!filterRole,
    !!filterDepartment,
    !!filterOffice,
    !!filterBlocked,
    !!filterSmartWorking,
  ].filter(Boolean).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <UsersLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Utenti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione utenti applicazione e permessi
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                const res = await api.post('/users/sync-remote');
                const { created = 0, updated = 0, errorsCount = 0 } = res.data || {};
                toast.success(`Sincronizzazione completata: creati ${created}, aggiornati ${updated}${errorsCount ? `, errori ${errorsCount}` : ''}`);
                fetchUsers();
              } catch (error) {
                console.error('Errore sincronizzazione utenti da remoto:', error);
                toast.error(error.response?.data?.message || 'Errore nella sincronizzazione dal DB remoto');
              }
            }}
            sx={{
              textTransform: 'none'
            }}
          >
            Sincronizza da DB remoto
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{ 
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 8px 16px 0 rgba(0, 171, 85, 0.24)',
              }
            }}
          >
            Nuovo Utente
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Cerca per nome o email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowFilters(!showFilters)}>
                    <Badge badgeContent={activeFiltersCount} color="primary">
                      <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                    </Badge>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        {showFilters && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-role-label">Ruolo</InputLabel>
                  <Select
                    labelId="filter-role-label"
                    value={filterRole}
                    label="Ruolo"
                    onChange={(e) => {
                      setFilterRole(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutti</em>
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-department-label">Dipartimento</InputLabel>
                  <Select
                    labelId="filter-department-label"
                    value={filterDepartment}
                    label="Dipartimento"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFilterDepartment(value);
                      setFilterOffice('');
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutti</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-office-label">Ufficio</InputLabel>
                  <Select
                    labelId="filter-office-label"
                    value={filterOffice}
                    label="Ufficio"
                    onChange={(e) => {
                      setFilterOffice(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutti</em>
                    </MenuItem>
                    {offices
                      .filter((office) =>
                        filterDepartment ? office.department_id === parseInt(filterDepartment, 10) : true
                      )
                      .map((office) => (
                        <MenuItem key={office.id} value={office.id}>
                          {office.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-blocked-label">Accesso</InputLabel>
                  <Select
                    labelId="filter-blocked-label"
                    value={filterBlocked}
                    label="Accesso"
                    onChange={(e) => {
                      setFilterBlocked(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutti</em>
                    </MenuItem>
                    <MenuItem value="blocked">Bloccati</MenuItem>
                    <MenuItem value="unblocked">Non bloccati</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filter-smart-working-label">Smart Working</InputLabel>
                  <Select
                    labelId="filter-smart-working-label"
                    value={filterSmartWorking}
                    label="Smart Working"
                    onChange={(e) => {
                      setFilterSmartWorking(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="">
                      <em>Tutti</em>
                    </MenuItem>
                    <MenuItem value="enabled">Abilitati</MenuItem>
                    <MenuItem value="disabled">Non abilitati</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedUsers.length > 0
              ? `Selezionati ${selectedUsers.length} utenti`
              : 'Nessun utente selezionato'}
            {totalItems > 0 && ` (totale ${totalItems} utenti con i filtri correnti)`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {totalItems > 0 && selectedUsers.length < totalItems && (
              <Tooltip title="Seleziona tutti gli utenti filtrati">
                <IconButton
                  color="primary"
                  onClick={handleSelectAllFilteredUsers}
                  sx={{
                    bgcolor: 'primary.50',
                    border: '1px solid',
                    borderColor: 'primary.200',
                    '&:hover': { bgcolor: 'primary.100' },
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {selectedUsers.length > 0 && (
              <Button
                variant="outlined"
                color="info"
                startIcon={<CheckSquare />}
                onClick={() => setBulkIpModalOpen(true)}
              >
                Aggiungi IP a {selectedUsers.length} utenti
              </Button>
            )}
            <Tooltip title="Assegna un dipartimento agli utenti selezionati (o filtrati)">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => setBulkDeptModalOpen(true)}
                  disabled={selectedUsers.length === 0 && totalItems === 0}
                  sx={{
                    bgcolor: 'info.50',
                    border: '1px solid',
                    borderColor: 'info.200',
                    '&:hover': { bgcolor: 'info.100' },
                  }}
                >
                  <BusinessIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Blocca o sblocca l’accesso agli account selezionati">
              <span>
                <IconButton
                  color="error"
                  onClick={() => setBulkBlockModalOpen(true)}
                  disabled={selectedUsers.length === 0 && totalItems === 0}
                  sx={{
                    bgcolor: 'error.50',
                    border: '1px solid',
                    borderColor: 'error.200',
                    '&:hover': { bgcolor: 'error.100' },
                  }}
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Abilita o disabilita Smart Working per gli utenti selezionati">
              <span>
                <IconButton
                  color="success"
                  onClick={() => setBulkSmartWorkingModalOpen(true)}
                  disabled={selectedUsers.length === 0 && totalItems === 0}
                  sx={{
                    bgcolor: 'success.50',
                    border: '1px solid',
                    borderColor: 'success.200',
                    '&:hover': { bgcolor: 'success.100' },
                  }}
                >
                  <LaptopMacIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

      {bulkSuccess && (
        <Typography color="success.main" sx={{ px: 3, pb: 2 }}>
            {bulkSuccess}
        </Typography>
      )}

      <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.neutral' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                    checked={users.length > 0 && selectedUsers.length === users.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'role_name'}
                    direction={orderBy === 'role_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('role_name')}
                  >
                    Ruolo
                  </TableSortLabel>
                </TableCell>
              <TableCell>Dipartimento</TableCell>
              <TableCell>Ufficio</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Data Creazione
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const isItemSelected = isSelected(user.id);
                const blockedFlag = user.block_access === 1 || user.block_access === true;
                const blockedByDate = user.block_date ? (new Date(user.block_date) <= new Date()) : false;
                const isAdmin = user.role_name === 'admin';
                const isBlocked = !isAdmin && (blockedFlag || blockedByDate);
                const isSmartWorkingEnabled = user.smart_working_enabled === 1 || user.smart_working_enabled === true;
                return (
                <TableRow 
                    key={user.id} 
                    hover
                    selected={isItemSelected}
                    onClick={(event) => handleClick(event, user.id)}
                    sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {user.name}
                    {isBlocked && (
                      <Tooltip title={`Accesso bloccato${user.block_date ? ` dal ${new Date(user.block_date).toLocaleString()}` : ''}`}>
                        <BlockIcon fontSize="small" color="error" />
                      </Tooltip>
                    )}
                    <Tooltip title={`Smart Working ${isSmartWorkingEnabled ? 'abilitato' : 'non abilitato'}`}>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', opacity: isSmartWorkingEnabled ? 1 : 0.35 }}>
                        <Laptop size={16} color={isSmartWorkingEnabled ? '#16a34a' : '#64748b'} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        backgroundColor: user.role_name === 'admin' ? 'rgba(255, 72, 66, 0.16)' : user.role_name === 'manager' ? 'rgba(255, 193, 7, 0.16)' : 'rgba(24, 144, 255, 0.16)',
                        color: user.role_name === 'admin' ? 'error.main' : user.role_name === 'manager' ? 'warning.main' : 'info.main',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        display: 'inline-block',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}
                    >
                      {user.role_name || 'N/A'}
                    </Box>
                  </TableCell>
                  <TableCell>{user.department_name || '-'}</TableCell>
                  <TableCell>{user.office_name || '-'}</TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleSmartWorkingClick(user)} color="info" title="Smart Working">
                      <Laptop size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpen(user)} color="primary">
                      <EditIcon size={18} />
                    </IconButton>
                    <IconButton size="small" onClick={() => openDeleteDialog(user)} color="error">
                      <DeleteIcon size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      Nessun utente trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledCard>

      <Modal open={open} onClose={handleClose}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" mb={3}>
            {currentId ? 'Modifica Utente' : 'Nuovo Utente'}
          </Typography>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Nome"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="role-label">Ruolo</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role_id"
                    name="role_id"
                    value={formik.values.role_id}
                    label="Ruolo"
                    onChange={formik.handleChange}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name.charAt(0).toUpperCase() + role.name.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="department-label">Dipartimento</InputLabel>
                  <Select
                    labelId="department-label"
                    id="department_id"
                    name="department_id"
                    value={formik.values.department_id}
                    label="Dipartimento"
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="">
                      <em>Nessuno</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!formik.values.department_id}>
                  <InputLabel id="office-label">Ufficio</InputLabel>
                  <Select
                    labelId="office-label"
                    id="office_id"
                    name="office_id"
                    value={formik.values.office_id}
                    label="Ufficio"
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="">
                      <em>Nessuno</em>
                    </MenuItem>
                    {availableOffices.map((office) => (
                      <MenuItem key={office.id} value={office.id}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label={currentId ? "Password (lascia vuoto per non cambiare)" : "Password"}
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="block-access-label">Blocca Accesso</InputLabel>
                  <Select
                    labelId="block-access-label"
                    id="block_access"
                    name="block_access"
                    value={formik.values.block_access ? 1 : 0}
                    label="Blocca Accesso"
                    onChange={(e) => {
                      const value = Number(e.target.value) === 1;
                      formik.setFieldValue('block_access', value);
                      if (!value) {
                        formik.setFieldValue('block_date', '');
                      } else {
                        if (!formik.values.block_date) {
                          const now = new Date();
                          const pad = (n) => String(n).padStart(2, '0');
                          const yyyy = now.getFullYear();
                          const mm = pad(now.getMonth() + 1);
                          const dd = pad(now.getDate());
                          const hh = pad(now.getHours());
                          const mi = pad(now.getMinutes());
                          formik.setFieldValue('block_date', `${yyyy}-${mm}-${dd}T${hh}:${mi}`);
                        }
                      }
                    }}
                    disabled={(roles.find(r => r.id === formik.values.role_id)?.name === 'admin')}
                  >
                    <MenuItem value={0}>No</MenuItem>
                    <MenuItem value={1}>Sì</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formik.values.block_access && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    id="block_date"
                    name="block_date"
                    label="Data ultimo blocco"
                    type="datetime-local"
                    value={formik.values.block_date || ''}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                    helperText="Se impostata nel passato/oggi, l’accesso è bloccato"
                    disabled={(roles.find(r => r.id === formik.values.role_id)?.name === 'admin')}
                  />
                </Grid>
              )}
            </Grid>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={handleClose} variant="outlined">
                Annulla
              </Button>
              <Button type="submit" variant="contained">
                {currentId ? 'Aggiorna' : 'Crea'}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <SmartWorkingModal
        open={smartWorkingModalOpen}
        onClose={() => setSmartWorkingModalOpen(false)}
        userId={selectedUserForSmartWorking?.id}
        userName={selectedUserForSmartWorking?.name}
      />

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Questa azione è irreversibile. Stai per eliminare l'utente:
          </DialogContentText>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
            {deleteTarget?.name || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {deleteTarget?.email || '-'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkIpModalOpen} onClose={() => setBulkIpModalOpen(false)}>
        <DialogTitle>Aggiungi IP a {selectedUsers.length} utenti</DialogTitle>
        <DialogContent>
            <DialogContentText>
            Inserisci l'indirizzo IP che vuoi aggiungere a tutti gli utenti selezionati.
            Questa operazione non rimuoverà gli IP esistenti.
            </DialogContentText>
            <TextField
            autoFocus
            margin="dense"
            id="bulk-ip"
            label="Indirizzo IP"
            type="text"
            fullWidth
            variant="outlined"
            value={bulkIpAddress}
            onChange={(e) => {
                setBulkIpAddress(e.target.value);
                setBulkError('');
            }}
            error={!!bulkError}
            helperText={bulkError}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setBulkIpModalOpen(false)}>Annulla</Button>
            <Button onClick={handleBulkIpSubmit} variant="contained" disabled={!bulkIpAddress}>
            Conferma
            </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeptModalOpen} onClose={() => setBulkDeptModalOpen(false)}>
        <DialogTitle>Assegna Dipartimento a {selectedUsers.length} utenti</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Seleziona il dipartimento da assegnare agli utenti selezionati. Gli altri campi non verranno modificati.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="bulk-dept-label">Dipartimento</InputLabel>
            <Select
              labelId="bulk-dept-label"
              value={bulkDeptId}
              label="Dipartimento"
              onChange={(e) => setBulkDeptId(e.target.value)}
            >
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeptModalOpen(false)}>Annulla</Button>
          <Button onClick={handleBulkDeptSubmit} variant="contained" disabled={!bulkDeptId}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkBlockModalOpen} onClose={() => setBulkBlockModalOpen(false)}>
        <DialogTitle>Blocco Account per {selectedUsers.length} utenti</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Scegli se bloccare o sbloccare gli account degli utenti selezionati (o di tutti gli utenti filtrati).
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="bulk-block-action-label">Azione</InputLabel>
            <Select
              labelId="bulk-block-action-label"
              value={bulkBlockAction}
              label="Azione"
              onChange={(e) => setBulkBlockAction(e.target.value)}
            >
              <MenuItem value="block">Blocca</MenuItem>
              <MenuItem value="unblock">Sblocca</MenuItem>
            </Select>
          </FormControl>
          {bulkBlockAction === 'block' && (
            <TextField
              fullWidth
              id="bulk-block-date"
              name="bulk_block_date"
              label="Data ultimo blocco"
              type="datetime-local"
              value={bulkBlockDate}
              onChange={(e) => setBulkBlockDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Se non impostata, verrà usata la data/ora corrente"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkBlockModalOpen(false)}>Annulla</Button>
          <Button onClick={handleBulkBlockSubmit} variant="contained">
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkSmartWorkingModalOpen} onClose={() => setBulkSmartWorkingModalOpen(false)}>
        <DialogTitle>Smart Working per {selectedUsers.length} utenti</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Scegli se abilitare o disabilitare Smart Working per gli utenti selezionati (o per tutti gli utenti filtrati).
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="bulk-smart-working-action-label">Azione</InputLabel>
            <Select
              labelId="bulk-smart-working-action-label"
              value={bulkSmartWorkingAction}
              label="Azione"
              onChange={(e) => setBulkSmartWorkingAction(e.target.value)}
            >
              <MenuItem value="enable">Abilita Smart Working</MenuItem>
              <MenuItem value="disable">Disabilita Smart Working</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSmartWorkingModalOpen(false)}>Annulla</Button>
          <Button onClick={handleBulkSmartWorkingSubmit} variant="contained" color="success">
            Conferma
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
